import { useState, useCallback, memo, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { ModalsType, GetAgendaRecordsRes, AgendaRecordItem } from 'types';
import { ActionModal } from 'components/mui/common/ActionModal';
import { RecordsFilterTable } from './RecordsFilterTable';

interface Props extends ModalsType {
  title: string;
  onSubmit: (selectedRecords: string[]) => void;
  agendaId: string;
  viewId: string;
  selectedRecordIds: string[];
  records: AgendaRecordItem[];
}

export const RecordsFilterModal = memo(({ open, title, onClose, onSubmit, agendaId, viewId, selectedRecordIds, records }: Props) => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<string[]>(selectedRecordIds);

  useEffect(() => {
    setSelectedRows(selectedRecordIds);
  }, [selectedRecordIds]);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 10,
    search: '',
    orderBy: '',
    orderDesc: false,
    formItemId: '',
  });

  const data = useMemo(() => {
    return {
      items: records,
      totalCount: records.length,
      page: tableState.page,
      pageSize: tableState.pageSize,
    } as GetAgendaRecordsRes<AgendaRecordItem[]>;
  }, [records, tableState.page, tableState.pageSize]);

  const handleSelectRows = useCallback((rowIds: string[]) => {
    setSelectedRows(rowIds);
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit(selectedRows);
  }, [onSubmit, selectedRows]);

  const handleStateChange = useCallback((newState: typeof tableState) => {
    setTableState(newState);
  }, []);

  return (
    <ActionModal
      open={open}
      title={title}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSubmit}
      maxWidth="lg"
      submitText={t('common.select')}
      sx={{
        zIndex: 12001,
      }}>
      <RecordsFilterTable
        agendaId={agendaId}
        viewId={viewId}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRows}
        data={data}
        isLoading={false}
        autoFocusSearch={true}
        onStateChange={handleStateChange}
      />
    </ActionModal>
  );
});
